import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, Divider, Avatar as AAvatar, Space, Affix, Tooltip } from 'antd';
import styled from 'styled-components';
import parse from 'html-react-parser';
import Link from 'next/link';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import type { TourProps } from 'antd';
import Breadcrumb from 'components/Breadcrumb';
import SectionTitle from 'components/Section/SectionTitle';
import Avatar from 'components/AuthorAvatar';
import ArticleContent from 'components/ArticleContent';
import AboutAuthorSection from './AboutAuthorSection';
import FormattedDate from 'components/FormattedDate';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import Section from 'components/Section';
import ArticleImage from 'components/ArticleImage';
import Image from 'components/Image';
import { merriweather } from 'styles/GlobalStyle';
import CardGrid from 'components/CardGrid';
import EventsBlock from 'components/EventsBlock';
import ContestSection from './ContestSection';

const ArticleCard = dynamic(() => import('components/ArticleCard'), { ssr: true });
const GalleryBlock = dynamic(() => import('components/GalleryBlock'), { ssr: true });
const VideoBlock = dynamic(() => import('components/VideoBlock'), { ssr: true });
const Tour = dynamic(() => import('antd/lib/tour'), { ssr: false });
const Newsletter = dynamic(() => import('containers/Newsletter'), { ssr: false });
const ArticleIssue = dynamic(() => import('components/ArticleIssue'), { ssr: false });
const ShareButtons = dynamic(() => import('components/ShareButtons'), { ssr: false });
const Subscription = dynamic(() => import('containers/Subscription'), {
    ssr: true
});
const Tags = dynamic(() => import('components/Tags'), {
    ssr: true
});

const SponsoredArticleSection = dynamic(() => import('./SponsoredArticleSection'), {
    ssr: true
});

const DynamicForm = dynamic(() => import('components/DynamicForm'), {
    ssr: false
});
const SkyscraperMobile = dynamic(() => import('components/Ads/SkyscraperMobile'), {
    ssr: false
});
const Rectangle = dynamic(() => import('components/Ads/Rectangle'), {
    ssr: false
});
const HalfPage = dynamic(() => import('components/Ads/HalfPage'), {
    ssr: false
});
const Wideboard = dynamic(() => import('components/Ads/Wideboard'), {
    ssr: false
});

const { Group: AvatarGroup } = AAvatar;

const AuthorSection = styled.div`
    color: ${(props) => props.color || props.theme.colors.primary};
    font-weight: 300;
    line-height: 18px;
    font-family: ${merriweather.style.fontFamily};
    position: absolute;
    top: -40px;
    left: 10px;
`;

const Title = styled.h1`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.41;
    letter-spacing: normal;
    text-align: left;
    color: #707070;
`;

const CompanyName = styled.h2`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.41;
    letter-spacing: normal;
    text-align: left;
    color: #707070;
`;

const AuthorLink = styled(Link)`
    color: ${(props) => props.color || props.theme.colors.primary};
    font-weight: 300;
    line-height: 18px;
    font-family: ${merriweather.style.fontFamily};
`;

const Date = styled.div``;

const CompanyDetailsText = styled.div`
    p {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        font-weight: 300;
        word-wrap: break-word;
        -webkit-line-clamp: 3; /* number of lines to show */
        -webkit-box-orient: vertical;
    }
`;

export interface Props {
    ads: PageAds;
    article: Article;
    content: string;
    category: Category[];
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    latestIssue: Issue;
    seeAlsoArticles: Article[];
    interestingArticles: Article[];
    singleGallery: Gallery;
    singleVideo: Video;
    topOneInCampaign: {
        articles: Article[];
        campaign: Campaign[];
    };
    topInCategory: Article[];
    whitePapers: WhitePaper[];
}

const ArticleDetails = ({
    ads,
    article,
    interestingArticles,
    category,
    content,
    topInCategory,
    events,
    eventsPatronate,
    singleGallery,
    seeAlsoArticles,
    singleVideo,
    latestIssue
}: Props): JSX.Element => {
    const { url } = getConfig().publicRuntimeConfig;

    const [tourOpen, setTourOpen] = useState(false);

    const contestSectionRef = useRef<HTMLDivElement>(null);

    const { rectangle, halfpage, skyscraper, wideboardBottom } = ads;

    const steps: TourProps['steps'] = [
        {
            title: 'Jak zagłosować',
            description: 'Chcesz zobaczyć w jaki sposób oddać głos na ten produkt?',
            target: null
        },
        {
            title: 'Oddaj swój głos!',
            description:
                'Kliknij "zagłosuj" aby oddać głos. Zostaniesz przeniesiony na stronę konkursu, gdzie możesz oddać pozostałe głosy na inne produkty.',
            target: () => contestSectionRef.current
        }
    ];

    useEffect(() => {
        setTourOpen(true);
    }, []);

    return (
        <>
            <Row
                gutter={16}
                style={{
                    flexWrap: 'wrap',
                    display: 'flex'
                }}
            >
                <Content>
                    <Row>
                        <Col span={24}>
                            <article>
                                <Row>
                                    <Col span={24}>
                                        <Breadcrumb paths={category} type="category" />
                                    </Col>
                                    <Col span={24}>
                                        <Title>{article.title}</Title>
                                    </Col>
                                    <Col span={24}>
                                        <ArticleImage
                                            type="opening"
                                            image={article.openingImage}
                                            alt={article.seoTitle}
                                            copyright={article.openingImageInfo?.copyright}
                                            width={885}
                                            height={500}
                                            priority
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <AuthorSection>
                                            <Row>
                                                <Col span={24}>
                                                    <AvatarGroup max={{ count: 3 }} size={74}>
                                                        {article.authors.map((author) => (
                                                            <Avatar
                                                                key={author.seoSlug}
                                                                author={author}
                                                                size={74}
                                                            />
                                                        ))}
                                                    </AvatarGroup>
                                                </Col>
                                                <Col span={24}>
                                                    {article.authors.map((author, i) => (
                                                        <>
                                                            <AuthorLink
                                                                key={`author-list-${author.seoSlug}`}
                                                                href={`/autorzy/${author.seoSlug}`}
                                                                passHref
                                                            >
                                                                {author.name}
                                                            </AuthorLink>
                                                            {i !== article.authors.length - 1 &&
                                                                ', '}
                                                        </>
                                                    ))}
                                                </Col>
                                                <Col span={24}>
                                                    <Date>
                                                        <FormattedDate
                                                            date={article.dateDisplayed}
                                                        />
                                                    </Date>
                                                </Col>
                                            </Row>
                                        </AuthorSection>
                                    </Col>
                                    <Col span={8}>
                                        <Section justify="end" $marginTop={25}>
                                            <Col>
                                                <ShareButtons
                                                    title={article.seoTitle || article.title}
                                                    teaser={
                                                        article.seoDescription || article.teaser
                                                    }
                                                    url={`${url}/artykuly/${article.seoSlug}`}
                                                />
                                            </Col>
                                        </Section>
                                    </Col>
                                    <Col span={24}>
                                        <Row>
                                            <Col xs={24} md={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[0]}
                                                    margin="35px auto 0 auto"
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[1]}
                                                    margin="35px auto 0  auto"
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col sm={24} md={20}>
                                        <ArticleContent
                                            interestingArticles={interestingArticles}
                                            content={content}
                                            article={article}
                                        />
                                    </Col>
                                </Row>
                                {article.contestCategories.length > 0 && (
                                    <>
                                        <Divider />
                                        <Section $marginTop={10} ref={contestSectionRef}>
                                            <Col span={24}>
                                                <SectionTitle level={2} title="Konkurs" />
                                                {article.contestCategories.map((category) => (
                                                    <ContestSection
                                                        key={
                                                            category.contest.seoSlug +
                                                            '--' +
                                                            category.id
                                                        }
                                                        category={category}
                                                        article={article}
                                                    />
                                                ))}
                                            </Col>
                                        </Section>
                                        <Tour
                                            steps={steps}
                                            open={tourOpen}
                                            onClose={() => setTourOpen(false)}
                                        />
                                    </>
                                )}

                                <Divider />

                                {article.issue && !article.sponsored && (
                                    <ArticleIssue issue={article.issue} />
                                )}

                                {article.authors.length > 0 && !article.sponsored && (
                                    <>
                                        <Section>
                                            <Col span={24}>
                                                <SectionTitle
                                                    title={
                                                        article.authors.length > 1
                                                            ? 'O Autorach'
                                                            : 'O Autorze'
                                                    }
                                                />
                                            </Col>
                                            <Col span={24}>
                                                <Row>
                                                    <Space
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    width: '30%',
                                                                    minWidth: '30%'
                                                                }}
                                                            />
                                                        }
                                                        direction="vertical"
                                                    >
                                                        {article.authors.map((author) => (
                                                            <Col span={24} key={author.seoSlug}>
                                                                <AboutAuthorSection
                                                                    author={author}
                                                                />
                                                            </Col>
                                                        ))}
                                                    </Space>
                                                </Row>
                                            </Col>
                                        </Section>
                                        <Divider />
                                    </>
                                )}
                                {article.sponsored &&
                                    article.companies.map((company) => {
                                        return (
                                            <Section gutter={16} key={company.seoSlug}>
                                                <Col span={24}>
                                                    <SectionTitle
                                                        level={2}
                                                        title="Materiał firmowy"
                                                        url={`/katalog-firm/${company.seoSlug}`}
                                                    />
                                                </Col>

                                                <Col span={24}>
                                                    <Image
                                                        type="logo"
                                                        src={company.logo}
                                                        alt={company.name}
                                                        width={90}
                                                        height={90}
                                                    />
                                                </Col>
                                                <Col span={24}>
                                                    <CompanyName>{company.name}</CompanyName>
                                                </Col>
                                                {company.about && (
                                                    <Col span={24}>
                                                        <CompanyDetailsText>
                                                            {parse(company.about)}
                                                        </CompanyDetailsText>
                                                    </Col>
                                                )}
                                            </Section>
                                        );
                                    })}

                                {!article.sponsored && article.tags.length > 0 && (
                                    <Section>
                                        <Col span={24}>
                                            <SectionTitle level={2} title="Tagi artykułu" />
                                        </Col>
                                        <Col style={{ marginTop: 10, paddingBottom: '40px' }}>
                                            <Tags tags={article.tags} />
                                        </Col>
                                    </Section>
                                )}
                            </article>
                            {!article.sponsored && seeAlsoArticles.length > 0 && (
                                <Row>
                                    <Col span={24}>
                                        <CardGrid
                                            type="article"
                                            items={seeAlsoArticles}
                                            title="Zobacz również"
                                        />
                                    </Col>
                                </Row>
                            )}
                            {!article.sponsored && (
                                <Section gutter={[16, 32]} justify="center">
                                    <Col span={24}>
                                        <Divider />
                                    </Col>
                                    <Col xs={24} md={20}>
                                        <Newsletter />
                                    </Col>
                                    <Col span={24}>
                                        <Divider />
                                    </Col>
                                </Section>
                            )}
                            {!article.sponsored && (
                                <Row>
                                    <Col span={24}>
                                        <Subscription issue={latestIssue} />
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Row>
                </Content>
                <Sider>
                    {article.sponsored && article.companies.length > 0 && (
                        <SponsoredArticleSection companies={article.companies} />
                    )}
                    {article.contestCategories.length > 0 && (
                        <Section $marginTop={10}>
                            <Col span={24}>
                                <SectionTitle level={2} title="Konkurs" />
                                {article.contestCategories.map((category) => (
                                    <ContestSection
                                        key={
                                            'sider__' +
                                            category.contest.seoSlug +
                                            '__' +
                                            category.id
                                        }
                                        category={category}
                                        article={article}
                                    />
                                ))}
                            </Col>
                        </Section>
                    )}
                    {!article.sponsored && topInCategory.length > 0 && (
                        <Section $marginTop={10}>
                            <Col span={24}>
                                <SectionTitle level={2} title="Top w kategorii" />
                                {topInCategory.map((article, index) => {
                                    const last = index === topInCategory.length - 1;
                                    return (
                                        <>
                                            <ArticleCard
                                                $article={article}
                                                key={`top-articles-${article.seoSlug}`}
                                                size={{ span: 24 }}
                                            />
                                            {!last && <Divider style={{ margin: '0 0 24px 0' }} />}
                                        </>
                                    );
                                })}
                            </Col>
                        </Section>
                    )}
                    {article.sideForm && article.sideForm.active && (
                        <Section>
                            {article.companies.length < 2 && (
                                <Affix offsetTop={article.companies.length > 0 ? 330 : 0}>
                                    <Col span={24}>
                                        <DynamicForm form={article.sideForm} />
                                    </Col>
                                </Affix>
                            )}
                            {article.companies.length >= 2 && (
                                <Col span={24}>
                                    <DynamicForm form={article.sideForm} />
                                </Col>
                            )}
                        </Section>
                    )}
                    {!article.sponsored && (
                        <Section $marginTop={10}>
                            <Col span={24}>
                                <Rectangle id={rectangle[0]} margin="16px auto" />
                            </Col>
                            <Col span={24}>
                                <HalfPage id={halfpage[0]} margin="16px auto" />
                            </Col>
                            <Col span={24}>
                                <Rectangle id={rectangle[1]} margin="16px auto" />
                            </Col>
                            <Col span={24}>
                                <Section>
                                    <Col span={24}>
                                        <EventsBlock
                                            events={events}
                                            eventsPatronate={eventsPatronate}
                                        />
                                    </Col>
                                </Section>
                            </Col>
                            <Col span={24}>
                                <HalfPage id={halfpage[1]} margin="16px auto" />
                            </Col>
                            <Col span={24}>
                                <Rectangle id={rectangle[2]} margin="16px auto" />
                            </Col>
                            <Divider />
                            {singleGallery && (
                                <Col span={24} style={{ marginTop: 15 }}>
                                    <GalleryBlock gallery={singleGallery} />
                                </Col>
                            )}
                            <Col span={24}>
                                <Rectangle id={rectangle[3]} margin="16px auto" />
                            </Col>
                            <Divider />
                            {singleVideo && (
                                <Col span={24} style={{ marginTop: 15 }}>
                                    <VideoBlock video={singleVideo} />
                                </Col>
                            )}
                            <Col span={24}>
                                <Rectangle id={rectangle[4]} margin="16px auto" />
                            </Col>
                            <Col span={24}>
                                <HalfPage id={halfpage[2]} margin="16px auto" />
                            </Col>
                            <Col span={24}>
                                <Rectangle id={rectangle[5]} margin="16px auto" />
                            </Col>
                        </Section>
                    )}
                </Sider>
                {!article.sponsored && (
                    <Col span={24}>
                        <Wideboard id={wideboardBottom[0]} />
                    </Col>
                )}
            </Row>
        </>
    );
};

export default ArticleDetails;
